<template>
  <div>
    <!-- Slot for your content -->
    <slot />
    
    <!-- Infinite scroll trigger element -->
    <div
      v-if="!hasReachedEnd && !isLoading"
      ref="scrollTrigger"
      class="h-4 w-full flex justify-center items-center"
    >
      <div
        v-if="isLoadingMore"
        class="loader flex items-center justify-center rounded-md"
      >
        <span
          class="w-6 aspect-square rounded-full border-4 border-[#d0cfcf] border-t-[#ffff] animate-spin"
        ></span>
      </div>
    </div>
    
    <!-- Optional: Show message when all content is loaded -->
    <div
      v-if="hasReachedEnd && !isLoading && endMessage !== 'No more content to load'"
      class="text-center text-gray-500 py-4"
    >
      <slot name="end-message">
        {{ endMessage }}
      </slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "InfiniteScroll",
  props: {
    // Function to load more data
    loadMore: {
      type: Function,
      required: true,
    },
    // Whether more data is being loaded
    isLoadingMore: {
      type: Boolean,
      default: false,
    },
    // Whether all data has been loaded
    hasReachedEnd: {
      type: Boolean,
      default: false,
    },
    // Whether the parent component is in initial loading state
    isLoading: {
      type: Boolean,
      default: false,
    },
    // Custom message when all content is loaded
    endMessage: {
      type: String,
      default: "No more content to load",
      required: false,
    },
    // Root margin for intersection observer (in pixels)
    rootMargin: {
      type: String,
      default: "100px",
    },
    // Threshold for intersection observer (0-1)
    threshold: {
      type: Number,
      default: 0.1,
    },
    // Whether to enable infinite scroll (useful for conditional enabling)
    enabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      observer: null,
      debounceTimer: null,
    };
  },
  mounted() {
    if (this.enabled) {
      this.setupInfiniteScroll();
    }
  },
  beforeUnmount() {
    this.cleanupInfiniteScroll();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  watch: {
    hasReachedEnd(newVal) {
      if (newVal) {
        this.cleanupInfiniteScroll();
      } else if (this.enabled) {
        this.setupInfiniteScroll();
      }
    },
    enabled(newVal) {
      if (newVal) {
        this.setupInfiniteScroll();
      } else {
        this.cleanupInfiniteScroll();
      }
    },
  },
  methods: {
    setupInfiniteScroll() {
      if (this.hasReachedEnd || this.isLoading || !this.enabled) {
        return;
      }

      // Clean up existing observer
      this.cleanupInfiniteScroll();

      // Create new Intersection Observer
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              !this.isLoadingMore &&
              !this.hasReachedEnd &&
              this.enabled
            ) {
              this.loadNextPage();
            }
          });
        },
        {
          rootMargin: this.rootMargin,
          threshold: this.threshold,
        }
      );

      // Observe the scroll trigger element
      if (this.$refs.scrollTrigger) {
        this.observer.observe(this.$refs.scrollTrigger);
      }
    },

    cleanupInfiniteScroll() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },

    loadNextPage() {
      // Clear any existing timer
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      // Debounce the load more call
      this.debounceTimer = setTimeout(() => {
        if (typeof this.loadMore === 'function' && !this.isLoadingMore) {
          console.log('🔄 InfiniteScroll: Triggering loadMore after debounce');
          this.loadMore();
        }
      }, 300); // 300ms debounce
    },
  },
};
</script>

<style lang="scss" scoped>
.loader {
  .animate-spin {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 